import React from "react";
import { Check, Lock, Gift } from "lucide-react";

// Horizontal creator-level ladder: New Creator -> World Builder -> Game Maker ->
// Featured Creator -> Master Creator -> GameView Partner. Driven by the API's
// creator_tier_ladder (each: label/emoji/color/min_plays/reached/current).
export function CreatorLadder({ ladder, current }) {
  if (!ladder?.length) return null;
  const next = ladder.find((t) => !t.reached);
  const plays = current?.plays ?? 0;
  const toNext = next ? Math.max(0, (next.min_plays || 0) - plays) : 0;

  return (
    <div data-testid="creator-ladder">
      <div className="flex items-stretch gap-1 sm:gap-2 overflow-x-auto pb-1">
        {ladder.map((t, i) => (
          <div key={t.key} className="flex items-center min-w-0 shrink-0">
            <div
              className={`flex flex-col items-center gap-1 rounded-xl px-2.5 py-2 min-w-[76px] transition-all ${t.current ? "ring-2 scale-105" : ""}`}
              style={{
                background: t.reached ? `${t.color}1f` : "hsl(var(--muted))",
                boxShadow: t.current ? `0 0 0 2px ${t.color}` : "none",
              }}
              data-testid={`ladder-tier-${t.key}`}
              title={`${t.label}${t.min_plays ? ` · ${t.min_plays.toLocaleString()}+ plays` : ""}${t.reward_credits ? ` · +${t.reward_credits} credits` : ""}${t.featured ? " · Featured eligible" : ""}`}
            >
              <span className="relative text-xl leading-none">
                <span className={t.reached ? "" : "opacity-40 grayscale"}>{t.emoji}</span>
                {t.reached && !t.current && (
                  <span className="absolute -bottom-1 -right-1 w-3.5 h-3.5 rounded-full grid place-items-center" style={{ background: t.color }}>
                    <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                  </span>
                )}
                {!t.reached && (
                  <span className="absolute -bottom-1 -right-1 w-3.5 h-3.5 rounded-full grid place-items-center bg-[hsl(var(--muted-foreground))]/40">
                    <Lock className="w-2 h-2 text-white" />
                  </span>
                )}
              </span>
              <span
                className={`text-[10px] font-bold text-center leading-tight ${t.reached ? "" : "text-[hsl(var(--muted-foreground))]"}`}
                style={t.reached ? { color: t.color } : undefined}
              >
                {t.label}
              </span>
            </div>
            {i < ladder.length - 1 && (
              <div className={`w-3 sm:w-5 h-0.5 rounded-full ${ladder[i + 1].reached ? "" : "bg-[hsl(var(--border))]"}`}
                style={ladder[i + 1].reached ? { background: ladder[i + 1].color } : undefined} />
            )}
          </div>
        ))}
      </div>
      <p className="text-xs text-[hsl(var(--muted-foreground))] mt-2" data-testid="ladder-progress">
        {next
          ? <>Currently <span className="font-semibold" style={{ color: current?.color }}>{current?.label}</span> — {toNext.toLocaleString()} more plays to reach <span className="font-semibold">{next.label}</span> {next.emoji}</>
          : <>Top level reached — <span className="font-semibold" style={{ color: current?.color }}>{current?.label}</span> {current?.emoji}. Legendary! 🎉</>}
      </p>
      {next?.reward_credits > 0 && (
        <p className="text-xs mt-1.5 flex items-center gap-1.5 text-[hsl(var(--muted-foreground))]" data-testid="ladder-next-reward">
          <Gift className="w-3.5 h-3.5 text-amber-500 shrink-0" /> Unlock next: <span className="font-semibold text-[hsl(var(--foreground))]">{next.perk}</span>
        </p>
      )}
    </div>
  );
}

export default CreatorLadder;
